import { useEffect, useState } from 'react';
import {
  IAPIError,
  checkIsGoBackSIA,
  getDxlAppMock,
  isAppMock,
  setDxlCampaigns,
  useDXLCampaigns,
  usePage,
  getSelectedOfferIds,
  updateProduct,
} from '@vfit/consumer/data-access';
import {
  ERROR_CODES_DYNAMIC_SHOPPING_CART,
  FLOW_TYPE_DYNAMIC_SHOPPING_CART,
} from '@vfit/shared/models';
import { AppMobile, getFromLocalStorageByKey } from '@vfit/shared/data-access';
import {
  DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE,
  DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE_SUCCESS,
  retrieveCartInfo,
  getIAddConfiguration,
  iAddProductNotFound,
} from './useDynamicShoppingCartFlow.utils';
import {
  IUseDynamicShoppingCart,
  IUseDynamicShoppingCartResponse,
} from './useDynamicShoppingCartFlow.models';
import { useCoverageToolFw, useCoverageToolVf, useCoverageToolVfFw } from '../useCoverageTools';
import { useQueryClient } from 'react-query';

export function useDynamicShoppingCartFlow() {
  const queryClient = useQueryClient();
  const { products } = usePage();
  const [coverageToolFlowPayload, setCoverageToolFlowPayload] = useState<IUseDynamicShoppingCart>({
    product: undefined,
    onConfirmAddress: false,
    alternativeProductList: [],
  });
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [dynamicShoppingCartResponse, setDynamicShoppingCartResponse] =
    useState<IUseDynamicShoppingCartResponse>(DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE);

  const {
    isLoading: isLoadingCoverageToolVf,
    isSuccess: isSuccessCoverageToolVf,
    isError: isErrorCoverageToolVf,
  } = useCoverageToolVf({
    product: coverageToolFlowPayload?.product,
    offerId: coverageToolFlowPayload?.product?.offerId?.toString() || '',
    address: coverageToolFlowPayload?.address?.placeDetails,
    isAddressConfirmed:
      isAddressConfirmed && dynamicShoppingCartResponse?.coverageToolFlowType === 'STANDARD',
  });
  const {
    isLoading: isLoadingCoverageToolVfFw,
    isSuccess: isSuccessCoverageToolVfFw,
    isError: isErrorCoverageToolVfFw,
  } = useCoverageToolVfFw({
    product: coverageToolFlowPayload?.product,
    offerId: coverageToolFlowPayload?.product?.offerId?.toString() || '',
    address: coverageToolFlowPayload?.address?.placeDetails,
    isAddressConfirmed:
      isAddressConfirmed && dynamicShoppingCartResponse?.coverageToolFlowType === 'AMDOCS_FW',
  });
  const {
    isLoading: isLoadingCoverageToolFw,
    isSuccess: isSuccessCoverageToolFw,
    isError: isErrorCoverageToolFw,
  } = useCoverageToolFw({
    product: coverageToolFlowPayload?.product,
    offerId: coverageToolFlowPayload?.product?.offerId?.toString() || '',
    address: coverageToolFlowPayload?.address?.placeDetails,
    isAddressConfirmed:
      isAddressConfirmed && dynamicShoppingCartResponse?.coverageToolFlowType === 'FASTWEB',
  });
  const {
    isError: isErrorDXLCampaigns,
    isSuccess: isSuccessDXLCampaigns,
    isLoading: isLoadingDXLCampaigns,
    refetch: startDXLCampaigns,
  } = useDXLCampaigns();

  // region FLOWS

  const errorFlow = (errorCode: number) => {
    setDynamicShoppingCartResponse({
      ...dynamicShoppingCartResponse,
      isLoading: false,
      isSuccess: false,
      isError: true,
      flowType: coverageToolFlowPayload?.flowType || '',
      errorCode,
    });
  };

  // endregion

  useEffect(() => {
    if (isSuccessCoverageToolVf || isSuccessCoverageToolVfFw || isSuccessCoverageToolFw) {
      const offeringServiceAbility = getFromLocalStorageByKey('offeringServiceability');
      if (!(offeringServiceAbility as IAPIError)?.error) {
        if (AppMobile.checkIsApp()) {
          const updatedIAddConfigAfterService = getIAddConfiguration(products, queryClient);
          if (!updatedIAddConfigAfterService) {
            // NO PRODUCT FOUND
            errorFlow(ERROR_CODES_DYNAMIC_SHOPPING_CART.SERVICE_ABILITY_ERROR);
            iAddProductNotFound();
          } else {
            setDynamicShoppingCartResponse({
              ...DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE_SUCCESS,
              flowType: coverageToolFlowPayload?.flowType || '',
              product: updatedIAddConfigAfterService,
            });
          }
        } else {
          const { cmsId: cmsIdSelectedOffer } = getSelectedOfferIds();
          const alternativeProduct = products?.find(
            (productForSlug) => productForSlug?.cmsId == cmsIdSelectedOffer
          );
          const productToSave = alternativeProduct || coverageToolFlowPayload?.product;
          updateProduct(productToSave);
          setTimeout(() => {
            setDynamicShoppingCartResponse({
              ...DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE_SUCCESS,
              flowType: coverageToolFlowPayload?.flowType || '',
              product: { ...productToSave },
            });
          }, 800);
        }
      } else {
        // NO PRODUCT FOUND
        errorFlow(ERROR_CODES_DYNAMIC_SHOPPING_CART.SERVICE_ABILITY_ERROR);
      }
    } else if (isErrorCoverageToolVf || isErrorCoverageToolVfFw || isErrorCoverageToolFw) {
      errorFlow(ERROR_CODES_DYNAMIC_SHOPPING_CART.SERVICE_ABILITY_ERROR);
    }
  }, [isSuccessCoverageToolVf, isSuccessCoverageToolVfFw, isSuccessCoverageToolFw]);

  useEffect(() => {
    if (isLoadingCoverageToolVf || isLoadingCoverageToolVfFw || isLoadingCoverageToolFw) {
      setDynamicShoppingCartResponse({
        ...dynamicShoppingCartResponse,
        isLoading: true,
      });
    }
  }, [isLoadingCoverageToolVf, isLoadingCoverageToolVfFw, isLoadingCoverageToolFw]);

  useEffect(() => {
    if (dynamicShoppingCartResponse?.isSuccess) {
      setTimeout(() => {
        if (!checkIsGoBackSIA())
          window.history.pushState({}, document.title, window.location.pathname);
      }, 1000);
    }
  }, [dynamicShoppingCartResponse]);

  const preconditionFlow = () => {
    switch (coverageToolFlowPayload.flowType) {
      case FLOW_TYPE_DYNAMIC_SHOPPING_CART.SHOPPING_CART_FIXED:
        if (coverageToolFlowPayload?.product && coverageToolFlowPayload?.onConfirmAddress) {
          setIsAddressConfirmed(true);
        }
        break;
      case FLOW_TYPE_DYNAMIC_SHOPPING_CART.SHOPPING_CART_MOBILE:
        setDynamicShoppingCartResponse({
          ...DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE_SUCCESS,
          flowType: coverageToolFlowPayload?.flowType || '',
          product: coverageToolFlowPayload?.product,
        });
        break;
    }
  };

  useEffect(() => {
    if (isErrorDXLCampaigns) {
      if (AppMobile.checkIsApp()) {
        errorFlow(ERROR_CODES_DYNAMIC_SHOPPING_CART.ERROR_DXL_CAMPAIGNS);
      }
    }
  }, [isErrorDXLCampaigns]);

  useEffect(() => {
    if (isSuccessDXLCampaigns && !isLoadingDXLCampaigns) preconditionFlow();
  }, [isSuccessDXLCampaigns, isLoadingDXLCampaigns]);

  const retrieveAppConfiguration = () => {
    if (AppMobile.checkIsApp()) {
      if (isAppMock()) {
        setDxlCampaigns(getDxlAppMock());
        preconditionFlow();
      } else {
        startDXLCampaigns();
      }
    } else {
      preconditionFlow();
    }
  };

  useEffect(() => {
    if (coverageToolFlowPayload.flowType) {
      setDynamicShoppingCartResponse({
        ...dynamicShoppingCartResponse,
        coverageToolFlowType: coverageToolFlowPayload?.coverageToolFlowType || 'STANDARD',
        flowType: coverageToolFlowPayload?.flowType || '',
        product: coverageToolFlowPayload?.product,
      });
      retrieveAppConfiguration();
    }
  }, [coverageToolFlowPayload.flowType]);

  const initDynamicShoppingCart = async () => {
    const cartInfo = await retrieveCartInfo(products || []);
    if (!cartInfo) {
      setDynamicShoppingCartResponse({
        ...DEFAULT_DYNAMIC_SHOPPING_CART_RESPONSE_SUCCESS,
        forceInit: true,
      });
    } else {
      setCoverageToolFlowPayload(cartInfo);
    }
  };

  useEffect(() => {
    initDynamicShoppingCart();
  }, []);

  return dynamicShoppingCartResponse;
}
